import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';

/**
 * ContextSelector — seletor de contexto no HEADER estilo `.funnel` do mockup (R0.3).
 *
 * Substitui os rails/sidebars internos de contexto (FunnelsSidebar, PipelinesSidebar,
 * TeamsSidebar) conforme regra travada: "seletor de contexto SEMPRE no header".
 * Nome atual em bold + chevron → popover claro-fixo com a lista; engrenagem opcional
 * ao lado para a config do contexto.
 *
 * @example
 * <ContextSelector
 *   items={funnels.map(f => ({ id: f.id, name: f.name }))}
 *   selectedId={funnelId}
 *   onSelect={setFunnelId}
 *   onSettings={() => setSettingsOpen(true)}
 *   placeholder="Selecionar funil"
 * />
 */
export interface ContextSelectorItem {
  id: string;
  name: string;
  /** Dot colorido opcional ao lado do nome (cor do funil/pipeline). */
  color?: string;
  /** Contagem opcional exibida à direita do item na lista. */
  count?: number;
}

export interface ContextSelectorProps {
  items: ContextSelectorItem[];
  selectedId?: string | null;
  onSelect: (id: string) => void;
  /** Engrenagem de configuração do contexto (aparece só se fornecido). */
  onSettings?: () => void;
  placeholder?: string;
  /** Rodapé do popover (ex.: "＋ Novo funil"). */
  footer?: React.ReactNode;
  'data-testid'?: string;
}

export const ContextSelector: React.FC<ContextSelectorProps> = ({
  items,
  selectedId,
  onSelect,
  onSettings,
  placeholder = 'Selecionar',
  footer,
  'data-testid': dataTestId = 'context-selector',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Posição fixed do popover (portal) — clampada à viewport (regra #10: nunca vazar)
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });

  const selected = items.find((i) => i.id === selectedId) ?? null;

  const POPOVER_W = 256; // w-64

  const computePos = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    let left = rect.left;
    left = Math.min(left, window.innerWidth - POPOVER_W - 8);
    left = Math.max(8, left);
    setPos({ left, top: rect.bottom + 6 });
  };

  useLayoutEffect(() => {
    if (!open) return;
    computePos();
    window.addEventListener('resize', computePos);
    window.addEventListener('scroll', computePos, true);
    return () => {
      window.removeEventListener('resize', computePos);
      window.removeEventListener('scroll', computePos, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Clique-fora fecha o popover (portal-aware)
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      const inRoot = rootRef.current?.contains(t) ?? false;
      const inPopover = popoverRef.current?.contains(t) ?? false;
      if (!inRoot && !inPopover) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={rootRef} className="relative flex items-center gap-1 min-w-0">
      {/* `.funnel` do mockup: 36px, padding 0 10px, gap 7px, fs 16 semibold,
          tracking tight, max-w 220px; chevron muted */}
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className="ds-funnel inline-flex items-center gap-[7px] h-9 px-2.5 rounded-lg min-w-0 max-w-[220px] transition-colors"
        style={{ color: 'var(--theme-text)' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        data-testid={dataTestId}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="font-semibold text-[16px] tracking-tight truncate min-w-0">
          {selected?.name ?? placeholder}
        </span>
        <Icon
          name="chevron-down"
          className="w-4 h-4 flex-shrink-0 transition-transform"
          style={{
            color: 'var(--theme-muted-text)',
            transform: open ? 'rotate(180deg)' : undefined,
          }}
        />
      </button>

      {/* `.btn-icon` engrenagem: 36×36, muted */}
      {onSettings && (
        <button
          type="button"
          onClick={onSettings}
          className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          title="Configurar funil e etapas"
          data-testid={`${dataTestId}-settings`}
        >
          <Icon name="settings" className="w-[18px] h-[18px]" />
        </button>
      )}

      {open && createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[70] w-64 rounded-xl p-1.5 max-h-[60vh] overflow-y-auto"
          style={{
            left: pos.left,
            top: pos.top,
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 24px rgba(0,0,0,.12)',
          }}
          role="listbox"
          data-testid={`${dataTestId}-popover`}
        >
          {items.length === 0 && (
            <p className="px-2.5 py-2 text-ds-sm" style={{ color: '#94a3b8' }}>
              Nenhum item disponível
            </p>
          )}
          {items.map((item) => {
            const active = item.id === selectedId;
            return (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { onSelect(item.id); setOpen(false); }}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-ds-base font-medium text-left"
                style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                data-testid={`${dataTestId}-item-${item.id}`}
              >
                {item.color && (
                  <span className="w-[9px] h-[9px] rounded-[3px] flex-shrink-0" style={{ backgroundColor: item.color }} />
                )}
                <span className="truncate flex-1">{item.name}</span>
                {typeof item.count === 'number' && (
                  <span
                    className="text-ds-sm font-semibold rounded-full px-1.5 tabular-nums"
                    style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}
                  >
                    {item.count}
                  </span>
                )}
                {active && <Icon name="check" className="w-4 h-4 flex-shrink-0" style={{ color: '#8b5cf6' }} />}
              </button>
            );
          })}
          {footer && (
            <div
              className="mt-1 pt-1"
              style={{ borderTop: '1px solid #f1f5f9' }}
              // Ação de rodapé leva pra fora da lista (modal/drawer) — fecha o popover
              onClick={() => setOpen(false)}
            >
              {footer}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

ContextSelector.displayName = 'ContextSelector';
